import React, { useState, useEffect } from 'react';
import {
    Show,
    SimpleShowLayout,
    TextField,
    DateField,
    BooleanField,
    ReferenceField,
    FunctionField,
    useRecordContext,
    useNotify,
    useRefresh,
    useTranslate,
    usePermissions,
    useRedirect,
    EditButton,
} from 'react-admin';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Chip,
    Alert,
    Button,
    IconButton,
    Paper,
    Fab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    EvStation,
    LocationOn,
    BatteryChargingFull,
    Settings,
    QrCode,
    Info,
    Warning,
    CheckCircle,
    Error as ErrorIcon,
    CloudOff,
    RestartAlt,
    ArrowBack,
    Edit as EditIcon,
    Cable,
} from '@mui/icons-material';
import ChargerRebootDialog from './ChargerRebootDialog';
import { RebootAuditLog } from './RebootAuditLog';
import { PrintableQR } from './PrintableQR';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../utils/apiClient';

interface ActiveSession {
    sessionId: string;
    connectorId: number;
    status: string;
}

// Status color mapping
const getStatusColor = (status: string): { bg: string; text: string } => {
    const statusLower = (status || '').toLowerCase();
    if (['available'].includes(statusLower)) return { bg: '#E8F5E8', text: '#00C853' };
    if (['charging', 'preparing'].includes(statusLower)) return { bg: '#E3F2FD', text: '#2196F3' };
    if (['faulted', 'error', 'offline'].includes(statusLower)) return { bg: '#FFEBEE', text: '#F44336' };
    if (['maintenance', 'unavailable'].includes(statusLower)) return { bg: '#FFF3E0', text: '#FF9800' };
    return { bg: '#F5F5F5', text: '#757575' };
};

// Status icon mapping
const getStatusIcon = (status: string) => {
    const statusLower = (status || '').toLowerCase();
    if (['available'].includes(statusLower)) return <CheckCircle />;
    if (['offline'].includes(statusLower)) return <CloudOff />;
    if (['faulted', 'error'].includes(statusLower)) return <ErrorIcon />;
    return <Warning />;
};

// Normalize OCPP status (e.g. "SuspendedEVSE") to i18n key (e.g. "suspended_evse").
const getConnectorStatusKey = (status: string): string => {
    if (!status) return '';
    const s = status.toLowerCase();
    if (s === 'suspendedevse') return 'suspended_evse';
    if (s === 'suspendedev') return 'suspended_ev';
    return s;
};

// Connectors Section Component (read-only view of ocpp_connectors for a charger)
const ConnectorsInfoSection = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    if (!record) return null;
    const connectors: any[] = record.connectors || [];

    return (
        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Cable sx={{ mr: 2, color: '#00C853' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {translate('resources.chargers.edit.connectors')}
                    </Typography>
                </Box>

                {connectors.length === 0 ? (
                    <Alert severity="info">
                        {translate('resources.chargers.edit.noConnectors')}
                    </Alert>
                ) : isMobile ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {connectors.map((connector: any) => {
                            const statusColors = getStatusColor(connector.status);
                            return (
                                <Box
                                    key={connector.connectorId}
                                    sx={{
                                        border: '1px solid #E0E0E0',
                                        borderRadius: 1,
                                        p: 2,
                                        backgroundColor: '#FAFAFA',
                                    }}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                            #{connector.connectorId} · {connector.connectorType}
                                        </Typography>
                                        <Chip
                                            label={translate(
                                                `resources.chargers.connectorStatuses.${getConnectorStatusKey(connector.status)}`,
                                                { _: connector.status || '-' }
                                            )}
                                            size="small"
                                            sx={{
                                                backgroundColor: statusColors.bg,
                                                color: statusColors.text,
                                                fontWeight: 600,
                                            }}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                {translate('resources.chargers.fields.maxPower')}
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {connector.maxPower} kW
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                {translate('resources.chargers.fields.maxCurrent')}
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {connector.maxCurrent ? `${connector.maxCurrent} A` : '-'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    {(connector.chargerType || connector.errorCode) && (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                                            {connector.chargerType && (
                                                <Chip
                                                    label={connector.chargerType.displayName}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: connector.chargerType.colorCode
                                                            ? `${connector.chargerType.colorCode}20`
                                                            : '#E0E0E0',
                                                        color: connector.chargerType.colorCode || '#000',
                                                    }}
                                                />
                                            )}
                                            {connector.errorCode && connector.errorCode !== 'NoError' && (
                                                <Chip
                                                    icon={<ErrorIcon sx={{ fontSize: 14 }} />}
                                                    label={connector.errorCode}
                                                    size="small"
                                                    sx={{ backgroundColor: '#FFEBEE', color: '#F44336' }}
                                                />
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                ) : (
                    <TableContainer sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{translate('resources.chargers.fields.connectorId')}</TableCell>
                                    <TableCell>{translate('resources.chargers.fields.connectorType')}</TableCell>
                                    <TableCell>{translate('resources.chargers.fields.maxPower')}</TableCell>
                                    <TableCell>{translate('resources.chargers.fields.maxCurrent')}</TableCell>
                                    <TableCell>{translate('resources.chargers.fields.chargerType')}</TableCell>
                                    <TableCell>{translate('resources.chargers.fields.status')}</TableCell>
                                    <TableCell>{translate('resources.chargers.fields.errorCode')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {connectors.map((connector: any) => {
                                    const statusColors = getStatusColor(connector.status);
                                    return (
                                        <TableRow key={connector.connectorId}>
                                            <TableCell>#{connector.connectorId}</TableCell>
                                            <TableCell>{connector.connectorType}</TableCell>
                                            <TableCell>{connector.maxPower} kW</TableCell>
                                            <TableCell>{connector.maxCurrent ? `${connector.maxCurrent} A` : '-'}</TableCell>
                                            <TableCell>
                                                {connector.chargerType ? (
                                                    <Chip
                                                        label={connector.chargerType.displayName}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: connector.chargerType.colorCode
                                                                ? `${connector.chargerType.colorCode}20`
                                                                : '#E0E0E0',
                                                            color: connector.chargerType.colorCode || '#000',
                                                        }}
                                                    />
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        -
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={translate(
                                                        `resources.chargers.connectorStatuses.${getConnectorStatusKey(connector.status)}`,
                                                        { _: connector.status || '-' }
                                                    )}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: statusColors.bg,
                                                        color: statusColors.text,
                                                        fontWeight: 600,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {connector.errorCode && connector.errorCode !== 'NoError' ? (
                                                    <Chip
                                                        icon={<ErrorIcon sx={{ fontSize: 14 }} />}
                                                        label={connector.errorCode}
                                                        size="small"
                                                        sx={{ backgroundColor: '#FFEBEE', color: '#F44336' }}
                                                    />
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        -
                                                    </Typography>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </CardContent>
        </Card>
    );
};

// Printable QR Code section: renders QR codes pointing at
// charge.greenwiser.app/c/<code> for printing physical stickers.
//
// If maxSimultaneousConnectors <= 1, the charger can only serve one session
// at a time — no need to distinguish which connector the user is plugged
// into, so only the charger-level QR (XXXX) is generated.
//
// If maxSimultaneousConnectors > 1, multiple sessions can run in parallel,
// so each connector gets its own QR (XXXX-1, XXXX-2, ...) to make sure the
// user starts the session on the connector they're physically using.
const PrintableQRSection = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    if (!record || !record.qrCode) return null;
    const connectors: any[] = record.connectors || [];
    const chargerName = (record.name as string) || (record.qrCode as string);
    const maxSimultaneous = Number(record.maxSimultaneousConnectors ?? 1);
    const showPerConnector = maxSimultaneous > 1 && connectors.length > 0;
    return (
        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <QrCode sx={{ mr: 2, color: '#00C853' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {translate('resources.chargers.show.printableQR', { _: 'Printable QR Codes' })}
                    </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Print these QR codes for the physical charger. The URL format opens
                    the Green Wiser app for users with the app installed; otherwise it
                    redirects them to the App Store / Play Store.
                    <br />
                    {showPerConnector
                        ? 'This charger supports multiple simultaneous sessions, so each connector has its own QR (XXXX-1, XXXX-2, …) for the user to scan at the connector they are using.'
                        : 'This charger supports only one simultaneous session, so a single charger-level QR is enough.'}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'flex-start' }}>
                    <PrintableQR
                        qrCode={record.qrCode as string}
                        chargerName={chargerName}
                    />
                    {showPerConnector && connectors.map((connector: any) => (
                        <PrintableQR
                            key={connector.connectorId}
                            qrCode={record.qrCode as string}
                            connectorId={connector.connectorId}
                            chargerName={chargerName}
                        />
                    ))}
                </Box>
            </CardContent>
        </Card>
    );
};

// Page Header Component
const PageHeader = () => {
    const record = useRecordContext();
    const redirect = useRedirect();
    const translate = useTranslate();

    if (!record) return null;

    const statusColors = getStatusColor(record.status);

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: { xs: 1, sm: 2 },
            mb: { xs: 2, sm: 4 },
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                <IconButton
                    onClick={() => redirect('/chargers')}
                    size="large"
                    sx={{
                        flexShrink: 0,
                        backgroundColor: '#FFFFFF',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        '&:hover': {
                            backgroundColor: '#F5F5F5',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                        },
                    }}
                    title={translate('ra.action.back')}
                >
                    <ArrowBack />
                </IconButton>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.25rem', sm: '2.125rem' }, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                        {record.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                            {record.stationId} • {record.model}
                        </Typography>
                        <Chip
                            icon={getStatusIcon(record.status)}
                            label={translate(`resources.chargers.statuses.${record.status?.toLowerCase()}`, { _: record.status })}
                            size="small"
                            sx={{
                                backgroundColor: statusColors.bg,
                                color: statusColors.text,
                                fontWeight: 600,
                            }}
                        />
                    </Box>
                </Box>
            </Box>

            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <EditButton
                    label={translate('ra.action.edit')}
                    variant="contained"
                    sx={{
                        backgroundColor: '#00C853',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                        py: 1.5,
                        borderRadius: 2,
                        '&:hover': {
                            backgroundColor: '#00A843',
                            boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
                        },
                    }}
                />
            </Box>
        </Box>
    );
};

// Reboot Section Component
interface SmartChargingConfig {
    max_stack_level?: number | null;
    supported_feature_profiles?: string[];
    charging_schedule_allowed_charging_rate_unit?: string[];
    charging_schedule_max_periods?: number | null;
    max_charging_profiles_installed?: number | null;
    connector_switch_3to1_phase_supported?: boolean | null;
    queried_at?: string;
    firmware_version_at_query?: string | null;
    [key: string]: any;
}

const SmartChargingCapabilitiesSection = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    const notify = useNotify();
    const [config, setConfig] = useState<SmartChargingConfig | null>(
        (record?.smartChargingConfig as SmartChargingConfig | undefined) ?? null
    );
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setConfig((record?.smartChargingConfig as SmartChargingConfig | undefined) ?? null);
    }, [record?.smartChargingConfig]);

    if (!record) return null;

    const handleRefresh = async () => {
        setLoading(true);
        try {
            const response = await apiClient.post(
                `/api/backend/chargers/${record.id}/get-configuration`,
                {}
            ) as { smartChargingConfig?: SmartChargingConfig };
            if (response?.smartChargingConfig) {
                setConfig(response.smartChargingConfig);
                notify(
                    translate('resources.chargers.fields.smartChargingConfig.refreshSuccess'),
                    { type: 'success' }
                );
            } else {
                notify(
                    translate('resources.chargers.fields.smartChargingConfig.refreshFailed'),
                    { type: 'warning' }
                );
            }
        } catch (error: any) {
            notify(
                error?.message ||
                translate('resources.chargers.fields.smartChargingConfig.refreshFailed'),
                { type: 'error' }
            );
        } finally {
            setLoading(false);
        }
    };

    const renderValue = (v: any): string => {
        if (v === null || v === undefined) return '—';
        if (Array.isArray(v)) return v.length === 0 ? '—' : v.join(', ');
        if (typeof v === 'boolean') return v ? 'Yes' : 'No';
        return String(v);
    };

    const rows: Array<{ key: string; value: any }> = config
        ? [
            { key: 'maxStackLevel', value: config.max_stack_level },
            { key: 'supportedFeatureProfiles', value: config.supported_feature_profiles },
            { key: 'chargingRateUnits', value: config.charging_schedule_allowed_charging_rate_unit },
            { key: 'maxPeriods', value: config.charging_schedule_max_periods },
            { key: 'maxProfilesInstalled', value: config.max_charging_profiles_installed },
            { key: 'phaseSwitchSupported', value: config.connector_switch_3to1_phase_supported },
            { key: 'firmwareVersionAtQuery', value: config.firmware_version_at_query },
            { key: 'queriedAt', value: config.queried_at },
        ]
        : [];

    return (
        <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {translate('resources.chargers.fields.smartChargingConfig.description')}
            </Typography>

            {!config ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                    {translate('resources.chargers.fields.smartChargingConfig.notYetQueried')}
                </Alert>
            ) : (
                <TableContainer sx={{ mb: 2, overflowX: 'auto' }}>
                    <Table size="small">
                        <TableBody>
                            {rows.map((row) => (
                                <TableRow key={row.key}>
                                    <TableCell sx={{ fontWeight: 500, width: '40%' }}>
                                        {translate(`resources.chargers.fields.smartChargingConfig.${row.key}`)}
                                    </TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                                        {renderValue(row.value)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Button
                variant="outlined"
                onClick={handleRefresh}
                disabled={loading}
                sx={{ textTransform: 'none', fontWeight: 600 }}
            >
                {loading
                    ? translate('resources.chargers.fields.smartChargingConfig.refreshing')
                    : translate('resources.chargers.fields.smartChargingConfig.refreshButton')}
            </Button>
        </>
    );
};

const RebootChargerSection = () => {
    const record = useRecordContext();
    const [open, setOpen] = useState(false);
    const [activeSessions, setActiveSessions] = useState<ActiveSession[]>([]);
    const [loading, setLoading] = useState(false);
    const notify = useNotify();
    const translate = useTranslate();
    const refresh = useRefresh();

    const handleRebootClick = async () => {
        if (!record) return;

        try {
            setLoading(true);
            const response = await apiClient.get(
                `/api/backend/active-sessions?filter=${JSON.stringify({
                    charger_id: record.id,
                    status: ['preparing', 'charging', 'suspended', 'idle']
                })}`
            ) as { data?: any[] };

            const mappedSessions: ActiveSession[] = (response.data || []).map((session: any) => ({
                sessionId: session.app_session_id,
                connectorId: session.connector_id,
                status: session.status
            }));

            setActiveSessions(mappedSessions);
            setOpen(true);
        } catch (error: any) {
            console.error('Failed to check active sessions:', error);
            notify('Failed to check active sessions', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleSuccess = () => {
        notify(translate('resources.chargers.notifications.rebootSuccess'), { type: 'success' });
        refresh();
        setOpen(false);
    };

    if (!record) return null;

    const status = (record.status || '').toLowerCase();
    const chargerReportedStatus = (record.chargerReportedStatus || '').toLowerCase();
    const isChargerOffline =
        status === 'offline' ||
        status === 'unavailable' ||
        chargerReportedStatus === 'offline' ||
        chargerReportedStatus === 'unavailable';

    return (
        <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {translate('resources.chargers.edit.rebootDescription')}
            </Typography>

            {isChargerOffline && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {translate('resources.chargers.edit.chargerOfflineWarning')}
                </Alert>
            )}

            <Button
                variant="contained"
                color="warning"
                startIcon={<RestartAlt />}
                onClick={handleRebootClick}
                disabled={isChargerOffline || loading}
                sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                }}
            >
                {translate('resources.chargers.actions.reboot')}
            </Button>

            <ChargerRebootDialog
                open={open}
                charger={{
                    id: String(record.id),
                    name: record.name,
                    stationId: record.stationId,
                    status: record.status,
                    isConnected: !isChargerOffline,
                }}
                activeSessions={activeSessions}
                onClose={() => setOpen(false)}
                onSuccess={handleSuccess}
            />
        </>
    );
};

const EditFab = () => {
    const record = useRecordContext();
    const navigate = useNavigate();
    if (!record) return null;
    return (
        <Fab
            color="primary"
            onClick={() => navigate(`/chargers/${record.id}`)}
            sx={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                backgroundColor: '#009A75',
                '&:hover': { backgroundColor: '#007A5E' },
                zIndex: 1100,
            }}
        >
            <EditIcon />
        </Fab>
    );
};

export const ChargersShow = () => {
    const translate = useTranslate();
    const { permissions } = usePermissions();
    const canReboot = permissions?.includes('chargers:reboot');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <Show
            title={false}
            actions={false}
            component="div"
            sx={{
                maxWidth: '100%',
                overflow: 'hidden',
                backgroundColor: 'transparent',
                '& .RaShow-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
        >
            <SimpleShowLayout
                sx={{
                    px: { xs: 1, sm: 2 },
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                }}
            >
                <Box sx={{ width: '100%', maxWidth: 1200, margin: '0 auto' }}>
                    <PageHeader />

                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, flexWrap: 'wrap', gap: { xs: 0, sm: 3 } }}>
                        {/* Left Column */}
                        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 600px' } }}>
                            {/* Basic Information */}
                            <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                        <Info sx={{ mr: 2, color: '#00C853' }} />
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            {translate('resources.chargers.edit.basicInfo')}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 } }}>
                                        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' } }}>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                {translate('resources.chargers.fields.model')}
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                <TextField source="model" />
                                            </Typography>
                                        </Box>

                                        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' } }}>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                {translate('resources.chargers.fields.serialNumber')}
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                <TextField source="serialNumber" />
                                            </Typography>
                                        </Box>

                                        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' } }}>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                {translate('resources.chargers.fields.qrCode')}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <QrCode sx={{ fontSize: 20, color: '#757575' }} />
                                                <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                                                    <TextField source="qrCode" />
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>

                            {/* Location */}
                            <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                        <LocationOn sx={{ mr: 2, color: '#00C853' }} />
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            {translate('resources.chargers.edit.locationAssignment')}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 } }}>
                                        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' } }}>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                {translate('resources.chargers.fields.site')}
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                <ReferenceField source="siteId" reference="sites" link="show">
                                                    <TextField source="name" />
                                                </ReferenceField>
                                            </Typography>
                                        </Box>

                                        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' } }}>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                {translate('resources.chargers.fields.loadGroupId')}
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                <TextField source="loadGroupName" emptyText="-" />
                                            </Typography>
                                        </Box>

                                        <Box sx={{ flex: '1 1 100%' }}>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                {translate('resources.chargers.fields.physicalLocation')}
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                <TextField source="physicalLocation" />
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>

                            {/* Technical Specifications */}
                            <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                        <BatteryChargingFull sx={{ mr: 2, color: '#00C853' }} />
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            {translate('resources.chargers.edit.technicalSpecs')}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 } }}>
                                        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 150px' } }}>
                                            <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#E8F5E8' }}>
                                                <Typography sx={{ fontWeight: 600, color: '#00C853', fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                                                    <TextField source="maxPower" />
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {translate('resources.chargers.fields.maxPower')}
                                                </Typography>
                                            </Paper>
                                        </Box>

                                        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 150px' } }}>
                                            <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#E3F2FD' }}>
                                                <Typography sx={{ fontWeight: 600, color: '#2196F3', fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                                                    <TextField source="maxCurrent" />
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {translate('resources.chargers.fields.maxCurrent')}
                                                </Typography>
                                            </Paper>
                                        </Box>

                                        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 150px' } }}>
                                            <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#FFF3E0' }}>
                                                <Typography variant="body1" sx={{ fontWeight: 600, color: '#FF9800' }}>
                                                    <TextField source="power" />
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {translate('resources.chargers.show.display')}
                                                </Typography>
                                            </Paper>
                                        </Box>

                                        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 150px' } }}>
                                            <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#F3E5F5' }}>
                                                <Typography sx={{ fontWeight: 600, color: '#9C27B0', fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                                                    <TextField source="maxSimultaneousConnectors" />
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {translate('resources.chargers.fields.maxSimultaneousConnectors')}
                                                </Typography>
                                            </Paper>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Box>

                        {/* Right Column */}
                        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 400px' } }}>
                            {/* OCPP Configuration */}
                            <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                        <Settings sx={{ mr: 2, color: '#00C853' }} />
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            {translate('resources.chargers.edit.ocppConfig')}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                {translate('resources.chargers.fields.ocppVersion')}
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                <TextField source="ocppVersion" />
                                            </Typography>
                                        </Box>

                                        <Box>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                {translate('resources.chargers.fields.stationId')}
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                                                <TextField source="stationId" />
                                            </Typography>
                                        </Box>

                                        <Box>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                {translate('resources.chargers.fields.firmwareVersion')}
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                <TextField source="firmwareVersion" />
                                            </Typography>
                                        </Box>

                                        <Box>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                {translate('resources.chargers.fields.authEnabled')}
                                            </Typography>
                                            <FunctionField
                                                render={(record: any) => (
                                                    <Chip
                                                        icon={record.authEnabled ? <CheckCircle /> : <CloudOff />}
                                                        label={record.authEnabled ? translate('common.enabled') : translate('common.disabled')}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: record.authEnabled ? '#E8F5E8' : '#F5F5F5',
                                                            color: record.authEnabled ? '#00C853' : '#757575',
                                                        }}
                                                    />
                                                )}
                                            />
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>

                            {/* Status Information */}
                            <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                                        {translate('resources.chargers.show.statusInformation')}
                                    </Typography>

                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                {translate('resources.chargers.show.chargerReportedStatus')}
                                            </Typography>
                                            <FunctionField
                                                render={(record: any) => {
                                                    if (!record.chargerReportedStatus) return <Typography variant="body2" color="text.secondary">-</Typography>;
                                                    const colors = getStatusColor(record.chargerReportedStatus);
                                                    return (
                                                        <Chip
                                                            label={translate(`resources.chargers.statuses.${record.chargerReportedStatus?.toLowerCase()}`, { _: record.chargerReportedStatus })}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: colors.bg,
                                                                color: colors.text,
                                                                fontWeight: 600,
                                                            }}
                                                        />
                                                    );
                                                }}
                                            />
                                        </Box>

                                        <Box>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                {translate('resources.chargers.fields.errorCode')}
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                <TextField source="errorCode" emptyText="-" />
                                            </Typography>
                                        </Box>

                                        <Box>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                {translate('resources.chargers.fields.lastStatusUpdate')}
                                            </Typography>
                                            <Typography variant="body2">
                                                <DateField source="lastStatusUpdate" showTime />
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Box>
                    </Box>

                    {/* Connectors - Full Width */}
                    <ConnectorsInfoSection />

                    {/* Printable QR Codes - Full Width */}
                    <PrintableQRSection />

                    {/* Smart Charging Capabilities - Full Width */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Settings sx={{ mr: 2, color: '#1976d2' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {translate('resources.chargers.fields.smartChargingConfig.title')}
                                </Typography>
                            </Box>
                            <SmartChargingCapabilitiesSection />
                        </CardContent>
                    </Card>

                    {/* Reboot Section - Full Width */}
                    {canReboot && (
                        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                    <RestartAlt sx={{ mr: 2, color: '#FF9800' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        {translate('resources.chargers.edit.rebootCharger')}
                                    </Typography>
                                </Box>
                                <RebootChargerSection />
                            </CardContent>
                        </Card>
                    )}

                    {/* Reboot Audit History */}
                    {canReboot && <RebootAuditLogWrapper />}

                    {/* Metadata */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                                {translate('resources.chargers.show.metadata')}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 } }}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                        {translate('resources.chargers.show.created')}
                                    </Typography>
                                    <Typography variant="body2">
                                        <DateField source="createdAt" showTime />
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                        {translate('resources.chargers.show.lastUpdated')}
                                    </Typography>
                                    <Typography variant="body2">
                                        <DateField source="updatedAt" showTime />
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </SimpleShowLayout>
            {isMobile && <EditFab />}
        </Show>
    );
};

// Wrapper component to access record context for audit log
const RebootAuditLogWrapper = () => {
    const record = useRecordContext();
    if (!record) return null;
    return <RebootAuditLog chargerId={String(record.id)} />;
};
